import {OpenGraph} from 'next/dist/lib/metadata/types/opengraph-types'

import {generateDefaultOpenGraphMetadata} from '@/app/lib/helpers'
import {ArticleAuthor, BreadcrumbStep} from '@/app/lib/structuredData'
import {team} from '@/content/team'
import {Locale} from '@/utils/constants'
import {Author, Blog, BlogOverview, IndustryName, IndustryTopicName} from '@/utils/contentful'
import {getAbsoluteUrl} from '@/utils/i18n'
import {routeTo} from '@/utils/router'

type Translate = ((key: string) => string) & {has: (key: string) => boolean}

// The nav's label, so the trail names the blog the same way.
const BLOG_LABEL_KEY = 'page.menu-resources-blogs-title'

const INDUSTRY_LABEL_KEY = 'blog.industry-name'
const TOPIC_LABEL_KEY = 'blog.topic-hero-title'

export type BlogTrailParent = {industry: IndustryName} | {topic: IndustryTopicName}

export const getIndustryLabel = (t: Translate, industry: IndustryName) =>
  t(`${INDUSTRY_LABEL_KEY}-${industry}`)

export const getTopicLabel = (t: Translate, topic: IndustryTopicName) =>
  t(`${TOPIC_LABEL_KEY}-${topic}`)

// Contentful's fallback bucket, not a browsable category (/blog/category/general 404s). Its
// articles keep their URLs, with no section above them in the trail.
export const GENERAL_INDUSTRY = 'general'

const toParentStep = (t: Translate, parent: BlogTrailParent): BreadcrumbStep | null => {
  if ('industry' in parent) {
    return parent.industry === GENERAL_INDUSTRY
      ? null
      : {name: getIndustryLabel(t, parent.industry), path: routeTo.blog.industry(parent.industry)}
  }

  return {name: getTopicLabel(t, parent.topic), path: routeTo.blog.industryTopic(parent.topic)}
}

// One array drives both the visible trail and the BreadcrumbList, so the two cannot disagree.
export const buildBlogBreadcrumbTrail = (
  t: Translate,
  current: {title: string; path: string},
  parent?: BlogTrailParent,
): BreadcrumbStep[] => {
  const parentStep = parent ? toParentStep(t, parent) : null

  return [
    {name: t(BLOG_LABEL_KEY), path: routeTo.blog.home()},
    ...(parentStep ? [parentStep] : []),
    {name: current.title, path: current.path},
  ]
}

// Contentful authors are not linked to the team data, so profiles are matched on the displayed
// name. A miss just leaves the Person without a `url`.
export const toArticleAuthors = (t: Translate, authors: Author[]): ArticleAuthor[] =>
  authors.map(({name, role}) => {
    const member = Object.values(team).find(
      ({name: nameKey, slug}) => slug && t.has(nameKey) && t(nameKey).trim() === name.trim(),
    )

    return {
      name,
      ...(role ? {jobTitle: role} : {}),
      ...(member?.slug ? {profilePath: routeTo.aboutUs.teamMember(member.slug)} : {}),
    }
  })

// An asset whose file is missing parses to the literal 'https:undefined'.
export const usableImage = (image?: {url: string} | null) =>
  image?.url?.startsWith('https://') ? image : null

type ArticleOpenGraphArgs = {
  blog: Blog
  locale: Locale
  path: string
  authors: ArticleAuthor[]
}

// og:article:author takes profile URLs, so authors without a profile page are left out.
export const buildArticleOpenGraph = ({
  blog,
  locale,
  path,
  authors,
}: ArticleOpenGraphArgs): OpenGraph => {
  const image = usableImage(blog.ogImage) ?? usableImage(blog.image)
  const authorUrls = authors
    .filter(({profilePath}) => Boolean(profilePath))
    .map(({profilePath}) => getAbsoluteUrl(profilePath!, locale))

  return {
    // The generic site card, for an article without a usable image.
    ...generateDefaultOpenGraphMetadata(locale),
    type: 'article',
    url: getAbsoluteUrl(path, locale),
    ...(image ? {images: image} : {}),
    publishedTime: blog.date,
    ...(authorUrls.length > 0 ? {authors: authorUrls} : {}),
  }
}

// Topic articles live under their industry too; /blog/topic/* only lists them.
export const getBlogOverviewPath = (blog: BlogOverview) =>
  blog.industry
    ? routeTo.blog.industryArticle(blog.industry.name, blog.slug)
    : routeTo.blog.article(blog.slug)
